// The echo guard (Oct 1 2026, Wes's iPhone test): on a phone speaker Otto's own voice came back into the mic, the
// voice engine took it for the owner, and Otto cut himself off mid sentence. With AirPods it never happened.
//
// While Otto plays, the mic gate (micGate.ts) only opens for sound clearly louder than Otto's voice coming back. How
// much of his voice comes back is learned per device, all the time: the guard follows the lowest steady ratio of mic
// level to Otto's output level, starting strict (it falls quickly and rises slowly, so the owner talking over him
// barely moves it).
// AirPods learn almost no echo and keep the normal gate; a phone speaker learns a lot and asks the owner to be
// clearly louder than Otto to interrupt him. When Otto is quiet the guard is off and the normal gate alone runs.
//
// Pure logic on the audio clock (frame time), so it can be tested without a browser. The margin comes from
// @otto/config MicSettings.echo_guard_margin; the shapes below are tuning, not behavior.

export const ECHO_GUARD_TUNING = Object.freeze({
  /** Otto's level is held this long as it falls (the speaker to mic delay, Bluetooth included). */
  releaseMs: 300,
  /** Below this output RMS Otto counts as quiet and the guard is off. */
  minOutputRms: 0.004,
  /**
   * Otto's level assumed while his audio is arriving but not yet measured (Oct 2 2026: the player reports "playing"
   * a few frames after his first sound, and that first sound alone was enough to cut him off). Nothing is learned
   * from these frames.
   */
  pendingOutputRms: 0.05,
  /**
   * Mic RMS over Otto's output RMS before anything is learned: strict on purpose (more than a loud phone speaker).
   * It falls to the real echo within about a second of Otto's greeting; starting low would let a loud speaker
   * through for the first seconds, the very failure this guards against.
   */
  couplingStart: 1,
  couplingMin: 0.005,
  couplingMax: 3,
  /** Per 20 ms frame: falls fast toward a lower ratio, rises slowly (a time constant of about 20 s). */
  fall: 0.1,
  rise: 0.001,
});

export interface EchoGuard {
  /** The learned share of Otto's voice that comes back into the mic. */
  readonly coupling: number;
  /** The extra gate level for this frame (raw RMS), 0 when Otto is quiet or the guard is off. */
  threshold(micRms: number, ottoRms: number, nowMs: number, pending?: boolean): number;
}

export function createEchoGuard(margin: number): EchoGuard {
  const t = ECHO_GUARD_TUNING;
  let envelope = 0;
  let coupling: number = t.couplingStart;
  let lastAt: number | null = null;
  return {
    get coupling() {
      return coupling;
    },
    threshold(micRms, ottoRms, nowMs, pending = false) {
      const dt = lastAt === null ? 20 : Math.max(0, nowMs - lastAt);
      lastAt = nowMs;
      const out = Number.isFinite(ottoRms) && ottoRms > 0 ? ottoRms : 0;
      envelope = Math.max(out, envelope * Math.exp(-dt / t.releaseMs));
      if (!(margin > 0)) return 0;
      if (envelope < t.minOutputRms) return pending ? margin * coupling * t.pendingOutputRms : 0;
      const ratio = (Number.isFinite(micRms) && micRms > 0 ? micRms : 0) / envelope;
      const rate = ratio < coupling ? t.fall : t.rise;
      coupling = Math.min(t.couplingMax, Math.max(t.couplingMin, coupling + rate * (ratio - coupling)));
      return margin * coupling * envelope;
    },
  };
}
